import { useState, useEffect } from 'react';

const ContactSection: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('general');

  useEffect(() => {
    const handleOpenModal = (event: CustomEvent) => {
      setModalType(event.detail?.type || 'general');
      setIsModalOpen(true);
    };

    window.addEventListener('openContactModal', handleOpenModal as EventListener);
    
    return () => {
      window.removeEventListener('openContactModal', handleOpenModal as EventListener);
    };
  }, []);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const getModalTitle = () => {
    switch (modalType) {
      case 'gcc-markets':
        return 'GCC Markets Inquiry';
      case 'nyc-quote':
        return 'NYC Billboard Quote';
      case 'airport-solutions':
        return 'Airport Advertising Solutions';
      default:
        return 'Contact Us';
    }
  };

  return (
    <>
      <section id="contact" className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            {/* Content */}
            <div className="text-white space-y-6 sm:space-y-8">
              <div className="space-y-4 sm:space-y-6">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
                  Ready to Launch Your Global OOH Campaign?
                </h2>
                <p className="text-lg sm:text-xl text-gray-200 leading-relaxed">
                  Connect with our OOH specialists to discuss premium billboard placements across GCC, Asia, and New York markets. Get your custom campaign proposal today.
                </p>
              </div>

              {/* Contact Methods - Mobile Optimized */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20">
                  <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-phone-line text-white text-xl"></i>
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold mb-2">Direct Line</h3>
                  <p className="text-gray-300 text-sm sm:text-base">+971 4 123 4567</p>
                  <p className="text-gray-300 text-sm sm:text-base">+1 212 555 0123</p>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20">
                  <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-mail-line text-white text-xl"></i>
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold mb-2">Email Us</h3>
                  <p className="text-gray-300 text-sm sm:text-base">campaigns@oohglobal.com</p>
                  <p className="text-gray-300 text-sm sm:text-base">info@oohglobal.com</p>
                </div>
              </div>

              {/* CTA Button */}
              <div className="pt-4 sm:pt-6">
                <button 
                  onClick={() => setIsModalOpen(true)}
                  className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 sm:py-5 rounded-xl font-bold hover:shadow-2xl transition-all transform hover:scale-105 text-base sm:text-lg cursor-pointer"
                >
                  Start Your Campaign
                </button>
              </div>
            </div>

            {/* Image */}
            <div className="relative order-first lg:order-last">
              <div className="aspect-[4/3] bg-gradient-to-br from-blue-600/20 to-purple-600/20 rounded-2xl backdrop-blur-sm border border-white/10 overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/57139dc8dfb74929b1437cffdd3f6957.jpg"
                  alt="OOH Campaign Planning"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-md sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 sm:p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">{getModalTitle()}</h3>
                <button 
                  onClick={closeModal}
                  className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-600"></i>
                </button>
              </div>

              <form className="space-y-4 sm:space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                    <input 
                      type="text" 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                    <input 
                      type="text" 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input 
                    type="email" 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                    placeholder="john@company.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                  <input 
                    type="tel" 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Campaign Budget</label>
                  <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base pr-8">
                    <option>$50K - $100K</option>
                    <option>$100K - $500K</option>
                    <option>$500K - $1M</option>
                    <option>$1M+</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                  <textarea 
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base resize-none"
                    placeholder="Tell us about your campaign goals and target markets..."
                  ></textarea>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4">
                  <button 
                    type="submit"
                    className="flex-1 bg-blue-600 text-white py-3 sm:py-4 rounded-lg hover:bg-blue-700 transition-colors font-semibold text-base sm:text-lg cursor-pointer"
                  >
                    Send Inquiry
                  </button>
                  <button 
                    type="button"
                    onClick={closeModal}
                    className="flex-1 sm:flex-none border border-gray-300 text-gray-700 py-3 sm:py-4 px-6 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-base sm:text-lg cursor-pointer"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ContactSection;
